import React, { ReactNode } from 'react';
import SimpleMarkdown from '@khanacademy/simple-markdown';
import styled from 'styled-components';

import { Box, BoxProps, Heading, Paragraph, Text } from '@rover/kibble/core';

import { Props } from './Markdown.common';

const rules = {
  ...SimpleMarkdown.defaultRules,
  heading: {
    ...SimpleMarkdown.defaultRules.heading,
    react: (node, output, state) => {
      // Determine the size based on the level
      const size = (() => {
        if (node.level === 1) return '200';
        if (node.level === 2) return '100';
        if (node.level === 3) return '50';
        return undefined;
      })();

      // Use Heading for defined sizes, otherwise fallback to Text
      if (size) {
        return (
          <Heading key={state.key} size={size}>
            {output(node.content, state)}
          </Heading>
        );
      }

      return <Text key={state.key}>{output(node.content, state)}</Text>;
    },
  },
  link: {
    ...SimpleMarkdown.defaultRules.link,
    react: (node, output, state) => {
      return output(node.content, state);
    },
  },
  paragraph: {
    ...SimpleMarkdown.defaultRules.paragraph,
    react: (node, output, state) => {
      const children = output(node.content, state);
      // The `!children` may have unintended side effects. I don't undestand
      // the syntax tree in its full depth yet.
      if (!children || children.every((child) => child === null || child === undefined)) {
        return null;
      }

      return (
        <Paragraph key={state.key} data-testid="markdown-paragraph">
          {children}
        </Paragraph>
      );
    },
  },
  strong: {
    ...SimpleMarkdown.defaultRules.strong,
    react: (node, output, state) => {
      return (
        <Text key={state.key} fontWeight="semibold">
          {output(node.content, state)}
        </Text>
      );
    },
  },
  em: {
    ...SimpleMarkdown.defaultRules.em,
    react: (node, output, state) => {
      return (
        <Text key={state.key} sx={{ fontStyle: 'italic' }}>
          {output(node.content, state)}
        </Text>
      );
    },
  },
  u: {
    ...SimpleMarkdown.defaultRules.u,
    react: (node, output, state) => {
      return (
        <Text key={state.key} fontWeight="semibold">
          {output(node.content, state)}
        </Text>
      );
    },
  },
  list: {
    ...SimpleMarkdown.defaultRules.list,
    react: (node, output, state) => {
      const wrapper = node.ordered ? 'ol' : 'ul';

      return (
        <Box key={state.key} pb="3x" as={wrapper} start={node.start} data-testid="markdown-list">
          {node.items.map((item, i) => (
            <li>
              <Text key={i}>{output(item, state)}</Text>
            </li>
          ))}
        </Box>
      );
    },
  },
  image: {
    ...SimpleMarkdown.defaultRules.image,
    react: (_node, _output, _state) => {
      return null;
    },
  },
};

// @ts-expect-error - SimpleMarkdown types are incorrect
const parser = SimpleMarkdown.parserFor(rules);
const output = SimpleMarkdown.outputFor(rules, 'react');

/**
 * Keep internal spacing and let the user of this component decide the spacing
 * around the component.
 */
const NoSpacingBox = styled(Box)<BoxProps>`
  & > :first-child {
    padding-top: 0;
    margin-top: 0;
  }

  & > :last-child {
    padding-bottom: 0;
    margin-bottom: 0;
  }
`;

export default function Markdown({ children = '' }: Props): ReactNode {
  return <NoSpacingBox data-testid="markdown-wrapper">{output(parser(children))}</NoSpacingBox>;
}
